<template>
  <div class="service-search">
    <el-card class="search-card">
      <el-input
        v-model="keyword"
        placeholder="搜索社会服务机构"
        size="large"
        clearable
        @keyup.enter="handleSearch"
      >
        <template #append>
          <el-button :icon="Search" @click="handleSearch" />
        </template>
      </el-input>
    </el-card>

    <!-- 机构列表 -->
    <div class="service-list">
      <el-row :gutter="20">
        <el-col
          v-for="service in services"
          :key="service.id"
          :xs="24"
          :sm="12"
          :md="8"
        >
          <el-card class="service-card" shadow="hover" @click="goToDetail(service.id)">
            <div class="service-logo">
              <el-image
                :src="service.logo || '/static/images/default-org.png'"
                fit="cover"
              >
                <template #error>
                  <div class="image-slot">
                    <el-icon><OfficeBuilding /></el-icon>
                  </div>
                </template>
              </el-image>
            </div>
            
            <div class="service-info">
              <h3 class="service-name">{{ service.name }}</h3>
              
              <div class="service-meta">
                <div class="meta-item">
                  <el-icon><Location /></el-icon>
                  <span>{{ service.address }}</span>
                </div>
                <div class="meta-item">
                  <el-icon><Phone /></el-icon>
                  <span>{{ service.phone }}</span>
                </div>
              </div>
              
              <div class="service-desc">
                {{ service.description }}
              </div>
              
              <el-tag v-if="service.is_active" type="success" size="small">
                已激活
              </el-tag>
            </div>
          </el-card>
        </el-col>
      </el-row>

      <!-- 空状态 -->
      <el-empty v-if="!loading && services.length === 0" description="暂无服务机构" />
    </div>

    <!-- 分页 -->
    <div class="pagination-wrapper" v-if="total > 0">
      <el-pagination
        v-model:current-page="pagination.page"
        v-model:page-size="pagination.pageSize"
        :total="total"
        :page-sizes="[9, 18, 27, 36]"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handlePageChange"
      />
    </div>
  </div>
</template>

<script>
import serviceApi from '@/api/service'
import { Search, OfficeBuilding, Location, Phone } from '@element-plus/icons-vue'

export default {
  name: 'ServiceSearch',
  
  components: {
    // eslint-disable-next-line vue/no-unused-components
    Search,
    OfficeBuilding,
    Location,
    Phone
  },
  
  data() {
    return {
      keyword: '',
      services: [],
      total: 0,
      pagination: {
        page: 1,
        pageSize: 9
      },
      loading: false
    }
  },
  
  mounted() {
    this.loadServices()
  },
  
  methods: {
    /**
     * 加载服务机构列表
     */
    async loadServices() {
      this.loading = true
      
      try {
        const res = await serviceApi.searchServices({
          keyword: this.keyword,
          page: this.pagination.page,
          page_size: this.pagination.pageSize
        })
        
        if (res && res.code === 2000) {
          this.services = res.data.list || []
          this.total = res.data.total || 0
        }
      } catch (error) {
        console.error('加载服务机构失败:', error)
      } finally {
        this.loading = false
      }
    },
    
    /**
     * 搜索
     */
    handleSearch() {
      this.pagination.page = 1
      this.loadServices()
    },
    
    /**
     * 页码变化
     */
    handlePageChange(page) {
      this.pagination.page = page
      this.loadServices()
    },
    
    /**
     * 每页数量变化
     */
    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.pagination.page = 1
      this.loadServices()
    },
    
    /**
     * 跳转到详情
     */
    goToDetail(serviceId) {
      this.$router.push(`/service/detail/${serviceId}`)
    }
  }
}
</script>

<style lang="scss" scoped>
.service-search {
  min-height: 100vh;
  padding: 20px;
  background-color: #f5f7fa;
  
  .search-card {
    max-width: 800px;
    margin: 0 auto 30px;
  }
  
  .service-list {
    min-height: 400px;
    
    .service-card {
      margin-bottom: 20px;
      cursor: pointer;
      transition: transform 0.3s;
      
      &:hover {
        transform: translateY(-5px);
      }
      
      .service-logo {
        height: 150px;
        margin-bottom: 15px;
        border-radius: 4px;
        overflow: hidden;
        
        .el-image {
          width: 100%;
          height: 100%;
        }
        
        .image-slot {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
          background-color: #f5f7fa;
          color: #909399;
          font-size: 40px;
        }
      }
      
      .service-info {
        .service-name {
          margin: 0 0 15px 0;
          font-size: 18px;
          font-weight: 500;
          color: #303133;
        }
        
        .service-meta {
          margin-bottom: 15px;
          
          .meta-item {
            display: flex;
            align-items: center;
            gap: 5px;
            margin-bottom: 8px;
            font-size: 14px;
            color: #606266;
            
            .el-icon {
              color: #909399;
            }
            
            span {
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
          }
        }
        
        .service-desc {
          margin-bottom: 15px;
          font-size: 14px;
          color: #909399;
          line-height: 1.6;
          overflow: hidden;
          text-overflow: ellipsis;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
        }
      }
    }
  }
  
  .pagination-wrapper {
    display: flex;
    justify-content: center;
    margin-top: 30px;
  }
}

@media (max-width: 768px) {
  .service-search {
    padding: 10px;
  }
}
</style>
